package co.maytes.standupbot.dashboard.features.releases

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import co.maytes.standupbot.dashboard.components.motion.rotatesWhenOpen
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideChevronDown
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.ship.ShipEventDto
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import org.jetbrains.compose.web.dom.Button as HtmlButton
import org.jetbrains.compose.web.dom.Div

private const val SKELETON_ROWS = 8
private val skeletonTitleWidths = listOf(62, 48, 74, 55, 68, 44, 58, 70)

data class FeedListState(
    val events: List<ShipEventDto>,
    val grouping: FeedGrouping,
    val density: FeedDensity,
    val zone: TimeZone,
    val now: Instant,
    val today: LocalDate,
    val searching: Boolean,
    val expandedIds: Set<String>,
    val repoOverrides: Map<String, Boolean>,
)

data class FeedListActions(
    val onToggleRow: (String) -> Unit,
    val onToggleRepo: (String, Boolean) -> Unit,
    val onSetAllRepos: (List<String>, Boolean) -> Unit,
)

@Composable
fun ReleaseFeedList(
    state: FeedListState,
    actions: FeedListActions,
) {
    Div(
        attrs =
            Modifier.classNames(FeedClasses.FEED).toAttrs {
                attr("data-density", state.density.name.lowercase())
            },
    ) {
        when (state.grouping) {
            FeedGrouping.Date -> ByDay(state, actions)
            FeedGrouping.Repository -> ByRepository(state, actions)
        }
    }
}

@Composable
private fun ByDay(
    state: FeedListState,
    actions: FeedListActions,
) {
    val groups = remember(state.events, state.zone, state.today) { ReleaseFeedGroups.byDay(state.events, state.zone, state.today) }
    groups.forEach { group ->
        Div(attrs = Modifier.toAttrs { attr("role", "group") }) {
            DayHeading(label = group.label, count = group.events.size)
            group.events.forEach { event ->
                ShipEventRow(
                    event = event,
                    zone = state.zone,
                    now = state.now,
                    expanded = event.id in state.expandedIds,
                    onToggle = { actions.onToggleRow(event.id) },
                )
            }
        }
    }
}

@Composable
private fun ByRepository(
    state: FeedListState,
    actions: FeedListActions,
) {
    val groups = remember(state.events) { ReleaseFeedGroups.byRepo(state.events) }
    val openStates =
        groups.mapIndexed { index, group ->
            state.repoOverrides[group.repo] ?: ReleaseFeedGroups.defaultRepoOpen(index, groups.size, state.searching)
        }
    val allOpen = openStates.all { it }
    Div(
        attrs =
            Modifier
                .margin(bottom = Spacing.xs)
                .styleModifier {
                    property("display", "flex")
                    property("justify-content", "flex-end")
                }
                .toAttrs(),
    ) {
        Button(
            label = if (allOpen) "Collapse all" else "Expand all",
            variant = ButtonVariant.Ghost,
            size = ButtonSize.Sm,
            onClick = { actions.onSetAllRepos(groups.map { it.repo }, !allOpen) },
        )
    }
    groups.forEachIndexed { index, group ->
        val open = openStates[index]
        Div(attrs = Modifier.classNames(FeedClasses.ITEM).toAttrs()) {
            RepoGroupHeader(group, state, open) { actions.onToggleRepo(group.repo, !open) }
            LazyDisclosure(open = open) {
                group.events.forEach { event ->
                    ShipEventRow(
                        event = event,
                        zone = state.zone,
                        now = state.now,
                        expanded = event.id in state.expandedIds,
                        onToggle = { actions.onToggleRow(event.id) },
                    )
                }
            }
        }
    }
}

@Composable
private fun RepoGroupHeader(
    group: RepoGroup,
    state: FeedListState,
    open: Boolean,
    onToggle: () -> Unit,
) {
    HtmlButton(
        attrs =
            Modifier.classNames(FeedClasses.GROUP_HEAD).onClick { onToggle() }.toAttrs {
                attr("type", "button")
                attr("aria-expanded", open.toString())
            },
    ) {
        Box(modifier = Modifier.color(Palette.textTertiary).rotatesWhenOpen(open)) { LucideChevronDown(size = CHEVRON_SIZE) }
        SpanText(
            text = group.repo,
            modifier =
                Modifier
                    .fontSize(Typography.body.size)
                    .fontWeight(Typography.h4.weight)
                    .color(Palette.textPrimary)
                    .singleLine(),
        )
        SpanText(
            text = group.events.size.toString(),
            modifier =
                Modifier
                    .fontSize(Typography.caption.size)
                    .color(Palette.textTertiary)
                    .styleModifier { property("font-variant-numeric", "tabular-nums") },
        )
        SpanText(
            text = "Latest: ${group.latest.title}, ${ReleaseFeedGroups.relative(state.now, group.latest.occurredAt, state.zone)}",
            modifier =
                Modifier
                    .fontSize(Typography.bodySmall.size)
                    .color(Palette.textTertiary)
                    .singleLine()
                    .styleModifier {
                        property("flex", "1 1 0")
                        property("text-align", "right")
                    },
        )
    }
}

private const val CHEVRON_SIZE = 16

@Composable
fun ReleaseFeedSkeleton() {
    Div(attrs = Modifier.classNames(FeedClasses.FEED).toAttrs { attr("aria-busy", "true") }) {
        repeat(SKELETON_ROWS) { index ->
            Div(attrs = Modifier.classNames(FeedClasses.ITEM).toAttrs()) {
                Div(attrs = Modifier.classNames(FeedClasses.ROW).styleModifier {
                    property("cursor", "default")
                    property("min-height", "calc(${Typography.body.size} * 1.6 + ${Spacing.sm} * 2)")
                }.toAttrs()) {
                    SkeletonBar(fixed = true, modifier = Modifier.classNames(FeedClasses.ICON))
                    SkeletonBar(skeletonTitleWidths[index % skeletonTitleWidths.size], Modifier.classNames(FeedClasses.TITLE))
                    Div(attrs = Modifier.classNames(FeedClasses.META).toAttrs()) {
                        SkeletonBar(55)
                        SkeletonBar(70)
                        SkeletonBar(60, Modifier.classNames(FeedClasses.AUTHOR))
                        SkeletonBar(70, Modifier.classNames(FeedClasses.TIME))
                    }
                    SkeletonBar(fixed = true, modifier = Modifier.classNames(FeedClasses.CHEVRON))
                }
            }
        }
    }
}

@Composable
private fun SkeletonBar(
    widthPercent: Int = 0,
    modifier: Modifier = Modifier,
    fixed: Boolean = false,
) {
    Div(
        attrs =
            modifier
                .classNames(FeedClasses.BAR_FILL)
                .styleModifier {
                    property("width", if (fixed) "${Spacing.md}" else "$widthPercent%")
                    property("justify-self", "start")
                }
                .toAttrs { attr("aria-hidden", "true") },
    )
}
